import React, { useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star } from "lucide-react";

const RECO = [["go", "Favorable", "bg-[#059669] text-white border-[#059669]"], ["undecided", "Réservé", "bg-[#F59E0B] text-white border-[#F59E0B]"], ["no_go", "Défavorable", "bg-[#E60000] text-white border-[#E60000]"]];

export const EvaluationGrid = ({ open, onOpenChange, interview, criteria, onSaved }) => {
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [overall, setOverall] = useState("");
  const [reco, setReco] = useState("undecided");
  const [saving, setSaving] = useState(false);
  const ro = interview?.status === "done" && interview?.evaluation;
  const cur = ro ? interview.evaluation : { scores, comments, overall_comment: overall, recommendation: reco };
  const vals = Object.values(cur.scores || {});
  const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/recrutement/interviews/${interview.id}/evaluate`, { scores, comments, overall_comment: overall, recommendation: reco });
      toast.success(`Évaluation enregistrée — moyenne ${avg}/5`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="evaluation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black">Grille d'évaluation</DialogTitle>
          <DialogDescription className="text-xs">{interview?.candidate_name} · {interview?.offer_title}{ro ? ` · évalué par ${cur.by}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(ro ? cur.criteria || criteria : criteria).map((c) => (
            <div key={c.key} className="border border-[#DEE2E6] p-3" data-testid={`eval-row-${c.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold">{c.label}</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const on = (cur.scores || {})[c.key] >= n;
                    return (
                      <button key={n} type="button" disabled={ro} onClick={() => setScores((s) => ({ ...s, [c.key]: n }))} data-testid={`eval-score-${c.key}-${n}`} aria-label={`${n} sur 5`}
                              className={`h-8 w-8 border flex items-center justify-center transition ${on ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "bg-white border-[#DEE2E6] hover:border-[#F59E0B]"}`}>
                        <Star className="h-4 w-4" fill={on ? "currentColor" : "none"} />
                      </button>
                    );
                  })}
                </div>
              </div>
              {ro ? (cur.comments || {})[c.key] && <div className="text-xs text-[#495057] mt-2">{cur.comments[c.key]}</div> : (
                <Textarea value={comments[c.key] || ""} onChange={(e) => setComments((cm) => ({ ...cm, [c.key]: e.target.value }))} rows={1} placeholder="Commentaire (facultatif)" className="mt-2 text-xs" data-testid={`eval-comment-${c.key}`} />
              )}
            </div>
          ))}
          <div className="border border-[#DEE2E6] p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5">Recommandation</div>
            <div className="flex gap-1.5">
              {RECO.map(([k, l, cls]) => (
                <button key={k} type="button" disabled={ro} onClick={() => setReco(k)} data-testid={`eval-reco-${k}`}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${cur.recommendation === k ? cls : "bg-white border-[#DEE2E6] text-[#868E96]"}`}>{l}</button>
              ))}
            </div>
            {ro ? <div className="text-xs text-[#495057] mt-2 whitespace-pre-wrap">{cur.overall_comment}</div> : (
              <Textarea value={overall} onChange={(e) => setOverall(e.target.value)} rows={3} placeholder="Synthèse de l'entretien" className="mt-2 text-xs" data-testid="eval-overall" />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="font-display font-black text-2xl" data-testid="eval-average">{avg ? `${avg} / 5` : "— / 5"}</div>
            {!ro && <Button onClick={save} disabled={saving || !vals.length} className="bg-[#059669] text-white" data-testid="eval-save">{saving ? "Enregistrement…" : "Enregistrer l'évaluation"}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
